import { Settings } from "../../settings";
import { Citrix } from "./citrix-helpers";
import { Constants } from "./launch-constants";
import { Helpers } from "./launch-helpers";

let ui = {
    showProgress: () => {},
    hideProgress: () => {},
    setProgressText: () => {},
    clearErrors: () => {},
    currentRegion: null,
    excelChecked: false,
    application: null,
    applicationDisplay: null,
    autoLaunch: false,
};

export function createLaunchAPI({
    showProgress,
    hideProgress,
    setProgressText,
    clearErrors,
    currentRegion,
    excelChecked,
    application,
    applicationDisplay,
    autoLaunch,
}) {
    ui = {
        showProgress,
        hideProgress,
        setProgressText,
        clearErrors,
        currentRegion,
        excelChecked,
        application,
        applicationDisplay,
        autoLaunch,
    };

    return {
        handleLaunch,
        handleAutoLaunch,
        logoutAfterSessionTimeout,
        launchStoredResponseInBrowser,
    };
}

// Browser (HTML5) Launch State
let browserLaunchInfo = {
    launchId: null,
    tab: null,
};

// Store last launch response for potential browser fallback
let lastLaunchResponse = null;

// Exported Functions - called by launch-ui.js
export async function handleLaunch(requestedProtocol) {
    let url = buildLaunchAppUrl(
        requestedProtocol,
        ui.currentRegion,
        ui.excelChecked,
        ui.application,
        ui.applicationDisplay,
    );

    await performLaunch(requestedProtocol, url);
}

export async function handleAutoLaunch() {
    // Auto launch is essentially "Automated Testing"
    if (Settings.autoLaunch.enabled) {
        let appName = Settings.autoLaunch.appName;
        let appDisplay = Settings.autoLaunch.appDisplay;
        let protocol = Settings.autoLaunch.protocol;
        let useExcel = Settings.autoLaunch.launchExcel;

        const url = buildLaunchAppUrl(protocol, ui.currentRegion, useExcel, appName, appDisplay, 1);
        await performLaunch(protocol, url);
    }
}

export async function logoutAfterSessionTimeout() {
    let logoutUrl = Helpers.getRoutePath("logout");

    var sessionExpiration = Settings.session.expiration;
    setInterval(() => {
        var now = new Date();
        if (now >= sessionExpiration) {
            window.location.href = logoutUrl;
        }
    }, Settings.session.expirationCheckInterval);
}

export async function launchStoredResponseInBrowser() {
    if (!lastLaunchResponse) {
        throw new Error("No stored launch response available");
    }

    try {
        ui.showProgress();
        ui.setProgressText("Launching in browser...");

        // Open pre-launch tab for browser launch
        openPrelaunchTab(Constants.Protocols.ParallelsBrowser);

        // Use the stored response to launch in browser mode
        await launchInBrowser(lastLaunchResponse.clone());

        // Update progress bar with text, close once connected or timeout reached
        await startPostLaunchHandling();
    } catch (err) {
        closePrelaunchTab();
        await onLaunchException(err);
    }
}

// Main Launch Functions
function buildLaunchAppUrl(
    requestedProtocol,
    currentRegion,
    excelChecked,
    application,
    applicationDisplay,
    autoLaunch = 0,
) {
    const launchAppUrl = Helpers.getRoutePath("launch_app");

    return (
        `${launchAppUrl}?launchType=${requestedProtocol}&region=${currentRegion}&excel=${excelChecked}` +
        `&application=${application}&applicationDisplay=${applicationDisplay}&auto=${autoLaunch}`
    );
}

async function performLaunch(requestedProtocol, url) {
    try {
        ui.showProgress();

        if (Helpers.isBrowserLaunch(requestedProtocol)) {
            // To avoid browsers blocking the HTML5 tab, we open before fetch.
            // "User Activation": https://developer.chrome.com/blog/user-activation
            openPrelaunchTab(requestedProtocol);
        }

        // Clear any existing errors
        ui.clearErrors();

        // Call launch_controller.py::launch_app()
        const response = await fetch(url, { credentials: "same-origin", redirect: "follow" });
        const resultType = Helpers.getLaunchType(response);

        if (!response.ok || !Helpers.isSuccessResult(resultType)) {
            closePrelaunchTab();

            // Error handling:
            //   Standard "banner error" response will be a 302, which will be followed
            //   by fetch, so the response will be a 200 (text/html)
            await onLaunchError(response);
            return;
        }

        const progressLabel = Helpers.buildProgressText(resultType, response);
        ui.setProgressText(progressLabel);

        switch (resultType) {
            case Constants.ResultTypes.File:
                // Citrix, DCV File Based Client, RDP
                await downloadFile(response);
                break;

            case Constants.ResultTypes.ProtocolHandler:
                // Parallels Client, DCV Protocol Handler Client
                // Store response for potential browser fallback
                lastLaunchResponse = response.clone();
                await launchProtocolHandler(response);
                break;

            case Constants.ResultTypes.Browser:
                // HTML5 - Citrix, Parallels, DCV
                await launchInBrowser(response);
                break;

            default:
                throw new Error(`Launch result type '${resultType}' not supported`);
        }

        // Update progress bar with text, close once connected or timeout reached
        await startPostLaunchHandling();
    } catch (err) {
        closePrelaunchTab();
        await onLaunchException(err);
    }
}

async function startPostLaunchHandling() {
    if (Settings.launchStatusCheck.enabled) {
        // Start status check
        const checkStatusId = setInterval(
            () => checkLaunchStatus(checkStatusId),
            Settings.launchStatusCheck.interval,
        );

        // Timeout
        setTimeout(() => {
            clearInterval(checkStatusId);
            ui.hideProgress();
        }, Settings.launchStatusCheck.timeout);
    }
}

async function downloadFile(response) {
    const blob = await response.blob();
    const fileName = Helpers.getDownloadFileName(response);

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();

    URL.revokeObjectURL(url);
}

async function launchProtocolHandler(response) {
    let launchData;

    const mimeType = Helpers.getMimeType(response);
    switch (mimeType) {
        case Constants.MimeTypes.Json:
            // For Parallels, we could also pull "browser" from this, and
            // potentially show as an alternate launch (in progress bar)
            const { client: clientLaunch } = await getJsonResponse(response);
            if (!clientLaunch) {
                throw new Error("Client launch data not found");
            }
            launchData = clientLaunch;

            break;

        case Constants.MimeTypes.Text:
            launchData = await response.text();
            break;

        default:
            throw new Error(`Mime type '${mimeType}' not supported for protocol handler launch`);
    }

    window.location.href = launchData;

    /*
    TODO: Look at ways of detecting this didn't work

    Online sources will suggest a visibility change approach to determining success,
    but that is known to be fragile for various reasons.  As we already check if the user actually
    connected, it'll likely be better to leverage that.

    For Parallels, since we get back both a full client and browser launch string,
    we can utilize in UI; e.g. after X seconds switch the progress bar to
    something like "click here to launch in browser if you are having problems"
    */
}

async function launchInBrowser(response) {
    let launchData;

    const mimeType = Helpers.getMimeType(response);
    switch (mimeType) {
        case Constants.MimeTypes.Json:
            const { browser: browserLaunch } = await getJsonResponse(response);
            if (!browserLaunch) {
                throw new Error("Browser launch data not found");
            }
            launchData = browserLaunch;
            break;

        case Constants.MimeTypes.Text:
            launchData = await response.text();
            break;

        default:
            throw new Error(`Mime type '${mimeType}' not supported for browser launch`);
    }

    const protocol = Helpers.getProtocol(response);
    if (protocol === Constants.Protocols.CitrixBrowser) {
        Citrix.launchInBrowser(browserLaunchInfo.launchId, browserLaunchInfo.tab, launchData);
    } else {
        // Parallels and DCV
        browserLaunchInfo.tab.location.href = launchData;
    }
}

async function getJsonResponse(response) {
    const result = await response.json();
    if (!result) {
        throw new Error("Empty json response");
    }

    if (!("client" in result) && !("browser" in result)) {
        throw new Error("Malformed json response");
    }

    return result;
}

// HTML5 Tab Handling
function openPrelaunchTab(requestedProtocol) {
    const launchId = new Date().getTime();
    browserLaunchInfo.launchId = launchId;

    if (Helpers.protocolEquals(requestedProtocol, Constants.Protocols.CitrixBrowser)) {
        browserLaunchInfo.tab = window.open(Citrix.getCitrixHtml5Url(launchId), "_blank");
    } else {
        // TODO: Loading page requires more work
        const loadingPage = Helpers.getRoutePath("loading");
        browserLaunchInfo.tab = window.open(loadingPage, "_blank");
    }
}

function closePrelaunchTab() {
    if (browserLaunchInfo.tab) {
        browserLaunchInfo.tab.close();
    }
}

// Error Handling
async function onLaunchException(err) {
    try {
        console.log(`Exception: ${err}`);
        ui.hideProgress();

        // Attempt to show a generic banner instead of
        // immediately redirecting to unhandled error page
        const url = Helpers.getRoutePath("launch_error");
        const errorResponse = await fetch(url, { credentials: "same-origin" });

        await onLaunchError(errorResponse);
    } catch (err) {
        console.error(`Exception handling: ${err}`);

        // TODO: Add an id here that has some reference in Splunk (so likely passed from backend)
        document.location.href = Helpers.getRoutePath("error");
    }
}

async function onLaunchError(response) {
    if (response.redirected) {
        const url = new URL(response.url);
        if (Helpers.doesRelativeUrlEqual(url, "launch")) {
            // The response is a complete page, with error banners populated
            await renderResponseInPlace(response);
        } else if (Helpers.doesRelativeUrlEqual(url, "error")) {
            // Redirect to the "Unexpected error has occcurred" page
            document.location.href = response.url;
        }
    } else if (response.status === 401) {
        // TODO: What flow hits this?  Add comment
        const location = response.headers.get("Location");
        document.location.href = location ?? Helpers.getBasePath();
    } else {
        // TODO: What flow hits this?  Add comment
        document.location.href = Helpers.getRoutePath("error");
    }
}

async function renderResponseInPlace(response) {
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const base = doc.createElement("base");
    base.href = response.url || document.baseURI;
    doc.head.prepend(base);

    document.documentElement.innerHTML = doc.documentElement.innerHTML;

    for (const s of Array.from(document.scripts)) {
        const n = document.createElement("script");
        for (const { name, value } of Array.from(s.attributes)) n.setAttribute(name, value);
        n.textContent = s.textContent;
        s.replaceWith(n);
    }
}

// Launch Status
async function checkLaunchStatus(intervalId) {
    const url = Helpers.getRoutePath("is_session_active");

    try {
        const response = await fetch(url, { credentials: "same-origin" });
        if (response.status === 200) {
            const json = await response.json();

            if (json.is_active) {
                clearInterval(intervalId);
                ui.hideProgress();
            }
        } else {
            clearInterval(intervalId);

            // TODO: Review this, add comments
            let location = response.headers.get("Location");
            document.location.href = location ?? "/";
        }
    } catch (err) {
        clearInterval(intervalId);
        // TODO: Figure out how to handle this error
    }
}
